import { useState } from 'react';
import { useT } from '~/i18n';
import EuAuthorizationPicker from './EuAuthorizationPicker';
import { ImmigrationSaveError, saveEuAuthorization } from '../api/immigrationClient';
import { readEuAuthorization, type EuWorkAuthorization } from '../lib/immigration';
import type { JobPreferences } from '../lib/preferences';

/**
 * «¿Puedes trabajar legalmente en la Unión Europea?» en Perfil → Preferencias:
 * la misma pregunta del alta, para verla y cambiarla después (quien sacó la
 * residencia, por ejemplo). Sólo a quien eligió Europa.
 *
 * Guarda con su propio PATCH (sólo `eu_work_authorization`), como la tarjeta
 * H-2. A diferencia de esa, ESTA respuesta mueve el envío: sin papeles sólo
 * salen postulaciones a vacantes que aceptan candidatos de fuera de la UE.
 */
interface Props {
  preferences?: JobPreferences | null;
}

type Notice = { kind: 'saved' } | { kind: 'error'; message: string } | null;

export default function EuAuthorizationCard({ preferences }: Props) {
  const t = useT('onboarding').immigration;
  const [saved, setSaved] = useState<EuWorkAuthorization | null>(() => readEuAuthorization(preferences));
  const [value, setValue] = useState<EuWorkAuthorization | null>(saved);
  const [saving, setSaving] = useState(false);
  const [notice, setNotice] = useState<Notice>(null);

  const dirty = value !== saved;

  const save = async () => {
    if (!value) return;
    setSaving(true);
    setNotice(null);
    try {
      const stored = await saveEuAuthorization(value);
      const next = (stored && readEuAuthorization(stored)) ?? value;
      setSaved(next);
      setValue(next);
      setNotice({ kind: 'saved' });
    } catch (err) {
      setNotice({ kind: 'error', message: err instanceof ImmigrationSaveError ? err.message : t.saveFailed });
    } finally {
      setSaving(false);
    }
  };

  return (
    <section className="card" aria-labelledby="eu-authorization-title">
      <div className="card-body py-4 space-y-3">
        <div className="space-y-1">
          <h2 id="eu-authorization-title" className="font-semibold">{t.eu.cardTitle}</h2>
          <p className="text-sm hush">{t.eu.cardLede}</p>
        </div>

        <EuAuthorizationPicker
          showQuestion
          value={value}
          disabled={saving}
          onChange={(next) => {
            setValue(next);
            setNotice(null);
          }}
        />

        {dirty && value && (
          <button className="btn btn-primary btn-sm" onClick={save} disabled={saving}>
            {saving && <span className="loading loading-spinner loading-xs" />}
            {t.save}
          </button>
        )}

        {notice?.kind === 'saved' && (
          <p className="text-sm text-data" role="status">{t.eu.saved}</p>
        )}
        {notice?.kind === 'error' && (
          <p className="text-sm" role="alert" style={{ color: 'var(--coral-ink)' }}>
            {notice.message}
          </p>
        )}
      </div>
    </section>
  );
}
